import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * MaintenanceGate — wenn Wartungsmodus aktiv, blockt es das ganze Frontend
 * MIT AUSNAHME:
 *   - Admin-Dashboard (/admin/*)
 *   - Login-Route (/login) für Admins
 *   - Alle User mit role="admin"
 *
 * Owner-Regel Msg 2026-02: Notfall-Wartungsmodus für nicht-admin Frontend.
 * Prüft alle 30s neu, damit ein Admin die Wartung beenden kann und Nutzer
 * schnell wieder online kommen.
 */
export default function MaintenanceGate({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState({ enabled: false, message: "", estimated_end: "", pin_required: false });
  const [checked, setChecked] = useState(false);
  const [pinBypass, setPinBypass] = useState(false);
  const [showPinInput, setShowPinInput] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinError, setPinError] = useState("");
  const [verifying, setVerifying] = useState(false);

  const BYPASS_KEY = "maintenance_bypass_pin";

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/system/maintenance");
        if (cancelled) return;
        setStatus(data || { enabled: false });
        // Bereits gespeicherten PIN (Session) still gegenprüfen — verfällt automatisch,
        // wenn der Admin den PIN ändert.
        if (data?.enabled && data?.pin_required) {
          const stored = sessionStorage.getItem(BYPASS_KEY);
          if (stored) {
            try {
              const r = await api.post("/system/maintenance/verify-pin", { pin: stored });
              if (!cancelled) {
                if (r.data?.ok) setPinBypass(true);
                else { setPinBypass(false); sessionStorage.removeItem(BYPASS_KEY); }
              }
            } catch { /* ignore */ }
          }
        } else if (!cancelled) {
          setPinBypass(false);
        }
      } catch { /* Backend down oder Offline: kein Gate */ }
      finally { if (!cancelled) setChecked(true); }
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const submitPin = async () => {
    const pin = pinValue.trim();
    if (!pin) return;
    setVerifying(true); setPinError("");
    try {
      const { data } = await api.post("/system/maintenance/verify-pin", { pin });
      if (data?.ok) {
        sessionStorage.setItem(BYPASS_KEY, pin);
        setPinBypass(true);
      } else {
        setPinError(t("maintenance.pin_wrong"));
      }
    } catch {
      setPinError(t("maintenance.pin_wrong"));
    } finally { setVerifying(false); }
  };

  // Bypass für Admin-Bereiche + Admin-User + gültigen Session-PIN
  const isAdminRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/login");
  const isAdminUser = user?.role === "admin";
  const bypass = isAdminRoute || isAdminUser || pinBypass;

  if (!checked) return children; // Erst-Load ohne Blocker
  if (!status.enabled || bypass) return children;

  return (
    <div data-testid="maintenance-screen" className="fixed inset-0 z-[9999] bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-6">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        </div>
        <h1 className="text-2xl font-bold mb-2">{t("maintenance.back_soon")}</h1>
        <p className="text-sm text-white/70 leading-relaxed">
          {status.message || "Wir arbeiten gerade an der Plattform. Bitte hab ein paar Minuten Geduld — wir sind gleich wieder für dich da."}
        </p>
        {status.estimated_end && (
          <p className="text-xs text-white/50 mt-4">Geplantes Ende: {status.estimated_end}</p>
        )}

        {status.pin_required && (
          <div className="mt-8">
            {!showPinInput ? (
              <button
                data-testid="maintenance-pin-open"
                onClick={() => { setShowPinInput(true); setPinError(""); }}
                aria-label={t("maintenance.pin_have")}
                title={t("maintenance.pin_have")}
                className="mx-auto flex items-center justify-center w-8 h-8 rounded-full text-white/25 hover:text-white/70 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/></svg>
              </button>
            ) : (
              <div className="max-w-xs mx-auto">
                <div className="flex items-center gap-2">
                  <input
                    data-testid="maintenance-pin-input"
                    type="password"
                    autoFocus
                    value={pinValue}
                    maxLength={12}
                    onChange={(e) => { setPinValue(e.target.value); setPinError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") submitPin(); }}
                    placeholder={t("maintenance.pin_placeholder")}
                    className="flex-1 h-10 px-3 bg-white/10 border border-white/20 text-white text-sm text-center tracking-widest font-mono placeholder:text-white/30 focus:outline-none focus:border-white/50"
                  />
                  <button
                    data-testid="maintenance-pin-submit"
                    onClick={submitPin}
                    disabled={verifying || !pinValue.trim()}
                    className="h-10 px-4 bg-white text-[#0A0A0A] text-sm font-semibold disabled:opacity-40 hover:bg-white/90 transition-colors"
                  >
                    {verifying ? "…" : t("maintenance.pin_submit")}
                  </button>
                </div>
                {pinError && <p data-testid="maintenance-pin-error" className="text-xs text-red-400 mt-2">{pinError}</p>}
                <button
                  onClick={() => { setShowPinInput(false); setPinValue(""); setPinError(""); }}
                  className="text-[11px] text-white/40 hover:text-white/70 mt-3 transition-colors"
                >
                  {t("maintenance.pin_cancel")}
                </button>
              </div>
            )}
          </div>
        )}

        <p className="text-[10px] text-white/40 mt-8 tracking-wider">stilltouched</p>
      </div>
    </div>
  );
}
